import { useCallback } from 'react';

import type { ArtifactId } from '@/artifacts/navigation-map';
import { useGeoMarkupStore } from '@/store';
import type { Coordinates } from '@/types';
import { previewZoomBottomPadding } from '@/utils/zoomHelpers';

import { useArtifactNavigation } from './useArtifactNavigation';
import { useDeviceLayout } from './useDeviceLayout';

/**
 * Tab-aware navigation for list entry-points. If the clicked artifact already
 * has a tab open → switch to it; otherwise → close all tabs and open fresh.
 * In-panel cross-references use `useTaskDetailsPanelNavigation` instead, which
 * preserves the current tab while opening a new one.
 */
export const useTabAwareArtifactNavigation = () => {
  const { openArtifact } = useArtifactNavigation();
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);
  const { isMobile } = useDeviceLayout();

  const navigateFromList = useCallback(
    (artifactId: ArtifactId, identifier: string, coordinates?: Coordinates) => {
      const tabApi = useGeoMarkupStore.getState().taskNavigation;
      const existingTab = tabApi.getTabByIdentifier(identifier);

      if (existingTab) {
        tabApi.switchTab(existingTab.id);
        openArtifact(
          artifactId,
          'detail',
          existingTab.shortId ?? existingTab.taskId
        );
      } else {
        tabApi.closeAllTabs();
        openArtifact(artifactId, 'detail', identifier);
      }

      if (coordinates) {
        // Opening a detail panel on mobile triggers preview mode — reserve the
        // bottom padding so the geometry lands in the visible area, not behind
        // the panel that hasn't slid in yet.
        zoomTo(coordinates, {
          bottomPadding: previewZoomBottomPadding(isMobile),
        });
      }
    },
    [openArtifact, zoomTo, isMobile]
  );

  const navigateToTaskFromList = useCallback(
    (identifier: string, coordinates?: Coordinates) => {
      navigateFromList('tasks', identifier, coordinates);
    },
    [navigateFromList]
  );

  const navigateToNoteFromList = useCallback(
    (identifier: string, coordinates?: Coordinates) => {
      navigateFromList('notes', identifier, coordinates);
    },
    [navigateFromList]
  );

  return {
    navigateToTaskFromList,
    navigateToNoteFromList,
  };
};
